import { el } from "./ui/dom";
import { icon } from "./ui/icons";

// The secure link page, /s/<token> (brief 1B-4): no sign-in. It shows the business that sent it
// and the file(s) to open, or, for a readiness reminder, what the person still needs to upload.
// It works only until the link expires or is turned off; then it says so in plain words and shows
// nothing else. Every open is recorded by the server. An invoice sent with a pay link (brief 1E-3)
// shows Pay: what's open now, by card or bank, on Stripe's own page.

interface LinkView {
  from: string;
  title: string;
  purpose: "files" | "uploads";
  until: string;
  files: { id: string; name: string; size: number; parts: number }[];
  person: { firstName: string } | null;
  needs: { code: string | null; name: string; text: string }[];
  pay: { amount: string; invoice: string } | null;
}

const GONE =
  "This link doesn't work. It may have expired or been turned off. Ask whoever sent it for a new one.";

/** Over the core's rate limit (brief 1F-1): the server answers 429 with these words. */
const TOO_MANY = "Too many tries. Try again in a minute.";

const kb = (n: number) =>
  n >= 1024 * 1024
    ? `${(n / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(n / 1024))} KB`;

function page(view: HTMLElement, ...card: (Node | null)[]) {
  view.replaceChildren(
    el("main", { class: "signin-page linkpage" }, el("section", { class: "card signin" }, ...card)),
  );
}

async function save(token: string, f: LinkView["files"][number], said: HTMLElement) {
  const parts: ArrayBuffer[] = [];
  for (let n = 0; n < f.parts; n++) {
    const res = await fetch(`/api/s/${token}/files/${f.id}?part=${n}`, { cache: "no-store" });
    if (!res.ok) {
      said.textContent = res.status === 429 ? TOO_MANY : GONE;
      return;
    }
    parts.push(await res.arrayBuffer());
  }
  const a = el("a", { href: URL.createObjectURL(new Blob(parts)), download: f.name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

async function sendUpload(token: string, code: string, file: File): Promise<string | null> {
  const start = await fetch(`/api/s/${token}/uploads`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code, name: file.name, size: file.size }),
  });
  const s = (await start.json().catch(() => ({}))) as {
    upload?: string;
    parts?: number;
    partBytes?: number;
    error?: string;
  };
  if (!start.ok || !s.upload) return s.error ?? GONE;
  for (let n = 0; n < (s.parts ?? 1); n++) {
    const from = n * (s.partBytes ?? file.size);
    const r = await fetch(`/api/s/${token}/uploads/${s.upload}/${n}`, {
      method: "PUT",
      headers: { "content-type": "application/octet-stream" },
      body: file.slice(from, from + (s.partBytes ?? file.size)),
    });
    if (!r.ok) return ((await r.json().catch(() => ({}))) as { error?: string }).error ?? GONE;
  }
  const done = await fetch(`/api/s/${token}/uploads/${s.upload}/finish`, { method: "POST" });
  if (!done.ok) return ((await done.json().catch(() => ({}))) as { error?: string }).error ?? GONE;
  return null;
}

export async function showLink(view: HTMLElement, token: string) {
  let v: LinkView | null = null;
  let tooMany = false;
  try {
    const res = await fetch(`/api/s/${encodeURIComponent(token)}`, { cache: "no-store" });
    if (res.ok) v = (await res.json()) as LinkView;
    tooMany = res.status === 429;
  } catch {
    v = null;
  }
  if (tooMany) {
    page(
      view,
      el("h1", {}, "Too many tries"),
      el("p", { class: "note", role: "alert" }, icon("alert"), " ", TOO_MANY),
    );
    return;
  }
  if (!v) {
    page(
      view,
      el("h1", {}, "This link doesn't work"),
      el("p", { class: "note", role: "alert" }, icon("alert"), " ", GONE),
    );
    return;
  }
  const said = el("p", { class: "small", role: "status", "aria-live": "polite" });
  const lines =
    v.purpose === "files"
      ? v.files.map((f) => {
          const b = el(
            "button",
            { type: "button", class: "btn sm pri" },
            icon("download"),
            " Download",
          );
          b.addEventListener("click", () => void save(token, f, said));
          return el(
            "div",
            { class: "ptask", style: "justify-content:space-between" },
            el(
              "span",
              {},
              icon("file"),
              " ",
              el("b", {}, f.name),
              el("span", { class: "small muted" }, ` · ${kb(f.size)}`),
            ),
            b,
          );
        })
      : v.needs.map((n) => {
          const row = el("div", {
            class: "ptask",
            style: "justify-content:space-between;flex-wrap:wrap",
          });
          const label = el(
            "span",
            {},
            el("b", {}, n.name),
            el("div", { class: "small muted" }, n.text),
          );
          if (!n.code) {
            row.append(label, el("span", { class: "small muted" }, "Bring this to the office."));
            return row;
          }
          const id = `up-${n.code}`;
          const picker = el("input", {
            type: "file",
            id,
            hidden: true,
            accept: ".pdf,.jpg,.jpeg,.png,.heic,.webp",
          });
          const btn = el(
            "label",
            { class: "btn sm pri", for: id },
            icon("upload"),
            ` Upload ${n.name}`,
          );
          picker.addEventListener("change", async () => {
            const file = picker.files?.[0];
            if (!file) return;
            btn.replaceChildren(`Uploading ${file.name}…`);
            const error = await sendUpload(token, n.code as string, file);
            if (error) {
              btn.replaceChildren(icon("upload"), ` Upload ${n.name}`);
              said.replaceChildren(el("span", { class: "chip bad" }, icon("alert"), " ", error));
              return;
            }
            row.replaceChildren(
              label,
              el("span", { class: "chip ok" }, icon("check"), " Uploaded, waiting for review"),
            );
          });
          row.append(label, el("span", {}, btn, picker));
          return row;
        });
  // Pay online: the payer goes to Stripe's page and comes back here.
  const paid = new URLSearchParams(location.search).has("paid");
  let pay: HTMLElement | null = null;
  if (paid)
    pay = el(
      "p",
      { class: "note", role: "status" },
      icon("check"),
      " Thank you. Your payment is being processed; a receipt comes from Stripe.",
    );
  else if (v.pay) {
    const go = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("credit-card"),
      ` Pay ${v.pay.amount} online`,
    );
    go.addEventListener("click", async () => {
      go.disabled = true;
      const res = await fetch(`/api/s/${encodeURIComponent(token)}/pay`, { method: "POST" });
      const b = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
      if (res.ok && b.url) {
        location.assign(b.url);
        return;
      }
      go.disabled = false;
      said.replaceChildren(
        el("span", { class: "chip bad" }, icon("alert"), " ", b.error ?? "That didn't go through."),
      );
    });
    pay = el(
      "div",
      { class: "ptask", style: "justify-content:space-between;flex-wrap:wrap" },
      el(
        "span",
        {},
        el("b", {}, `Invoice ${v.pay.invoice}`),
        el("div", { class: "small muted" }, "Card or bank account, on Stripe's secure page"),
      ),
      go,
    );
  }
  page(
    view,
    el("div", { class: "eyebrow" }, v.from),
    el(
      "h1",
      {},
      v.title || (v.purpose === "uploads" ? "Documents to be ready to work" : "Documents"),
    ),
    el(
      "p",
      { class: "muted" },
      v.purpose === "uploads" && v.person
        ? `Hi ${v.person.firstName}, ${v.from} still needs these to get you ready to work. Upload each one here.`
        : `${v.from} sent you ${v.files.length === 1 ? "this document" : "these documents"}.`,
    ),
    el(
      "div",
      { class: "linkfiles" },
      ...(lines.length ? lines : [el("p", { class: "muted" }, "Nothing is waiting. Thank you.")]),
    ),
    pay,
    said,
    el(
      "p",
      { class: "small muted" },
      `This link is private to you and works until ${v.until}. Please don't forward it.`,
    ),
  );
}
